import Button from '../Buttons'
import { selectUser } from '../../redux/systemSlice'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/react'
import { DocumentState } from '../../redux/types'
import { selectDocumentState } from '../../redux/docSlice'
import { htmlToString } from '../../helpers/utility'
import { useDocId } from '../../hooks/docID'
import { useAuth } from '../../contexts/Auth'
import { useState } from 'react'
import { CONSTANTS } from '../../constants'
import RubricModal from '../Modals/Rubric'
import { TbReportAnalytics } from 'react-icons/tb'
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import useDocWordCount from '../../hooks/useDocWordCount'
import { FEATURE_FLAGS } from '../../hooks/featureFlags'

export type GraderRubric = {
	constructiveComments: string
	thesisStatement: string
	weakWords: string
	relevanceToTopic: number
	depthOfAnalysis: number
	evidenceAndSupport: number
	consistencyOfTone: number
	correctGrammar: number
	correctSpelling: number
	goodWordChoice: number
	qualityOfParagraphTransitions: number
}

export interface NewRubricScore {
	constructiveComments: string
	contentAndAnalysis: {
		score: number
		explanation: string
	}
	organizationAndStructure: {
		score: number
		explanation: string
	}
	languageAndStyle: {
		score: number
		explanation: string
	}
	criticalThinking: {
		score: number
		explanation: string
	}
	citationsAndReferences: {
		score: number
		explanation: string
	}
	suggestion1: {
		suggestion: string
		category: string
	}
	suggestion2: {
		suggestion: string
		category: string
	}
	suggestion3: {
		suggestion: string
		category: string
	}
	suggestion4: {
		suggestion: string
		category: string
	}
}

const Grader = ({ children }: { children?: React.ReactNode }) => {
	const [modalOpen, setModalOpen] = useState(false)
	const [scoreLoading, setScoreLoading] = useState(false)
	const [gradeResult, setGradeResult] = useState<GraderRubric | null>(null)
	const [newRubricScore, setNewRubricScore] = useState<NewRubricScore | undefined>(undefined)
	const docID = useDocId()
	const { currentUser } = useAuth() as any
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docID))
	const user = useSelector(selectUser)
	const posthog = usePostHog()
	const wordCount = useDocWordCount()
	const graderV3 = useFeatureFlagVariantKey(FEATURE_FLAGS.GRADER_V3)

	const handleGrade = async () => {
		posthog.capture('grade-essay')
		setScoreLoading(true)
		setModalOpen(true)

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				isV2: graderV3 === 'test',
				topic: htmlToString(documentState.title),
				essay: htmlToString(documentState.content),
				userId: user.id,
			}),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/essay/grade/', requestOptions)
			.then((res) => res.json())
			.then((response: GraderRubric | NewRubricScore) => {
				if (graderV3 === 'test') {
					setNewRubricScore(response as NewRubricScore)
				} else {
					setGradeResult(response as GraderRubric)
				}
				setScoreLoading(false)
			})
			.catch((err) => {
				Sentry.captureException(err)
			})
	}

	return (
		<>
			<RubricModal
				isOpen={modalOpen}
				onClose={() => {
					setModalOpen(false)
				}}
				loading={scoreLoading}
				rubric={gradeResult}
				newRubricScore={newRubricScore}
			/>
			<div
				onClick={() => {
					posthog.capture('grade-essay-button-clicked')
					handleGrade()
				}}
			>
				{children || (
					<Button
						type="outline"
						disabled={
							documentState &&
							(documentState.isHumanizing ||
								documentState.isGenerating ||
								documentState.isTypingAddMore ||
								documentState.isTypingEssay ||
								documentState.content === '' ||
								documentState.content === CONSTANTS.EMPTY_DOCUMENT ||
								wordCount < 20)
						}
						onClick={() => {
							posthog.capture('grade-essay-button-clicked')
							handleGrade()
						}}
						className={`px-2 md:px-3 !py-1 !font-normal`}
					>
						<div className="flex gap-2 items-center">
							{TbReportAnalytics({ className: 'hidden md:block' })}
							<div>Grade</div>
						</div>
					</Button>
				)}
			</div>
		</>
	)
}

export default Grader
